import { useState } from 'react';
import { Cpu, Database, BookOpen, Bot, Rocket, Check, Lock, AlertTriangle, ArrowRight, Sparkles, CircleCheck, Wrench, ExternalLink } from 'lucide-react';

// 分组任务清单（Setup Checklist）：必做/按需 + 前置依赖确定性锁定 + 跳转到对应配置页
const GROUPS = [
  {
    name: '① 平台基础', sub: '平台跑起来的硬前置，必做',
    items: [
      { key: 'model', title: '模型接入', icon: Cpu, required: true, deps: [], page: '模型管理', tab: 'models',
        desc: '在【模型管理】注册并启用至少一个支持 Function Calling 的纯文本模型（超级智能体驱动）。如需知识库，再注册启用向量、重排模型。' },
    ],
  },
  {
    name: '② 知识增强', sub: '按需——使用知识库检索才做',
    items: [
      { key: 'kb', title: '知识库创建', icon: BookOpen, required: false, deps: ['model'], page: '知识库管理', tab: 'knowledge',
        desc: '创建知识库、上传文档、解析切片向量化。前置：在模型管理启用向量、重排模型（向量数据库于部署时已配置好，无需在平台单独配 RAG 引擎）。' },
    ],
  },
  {
    name: '③ 智能体接入', sub: '必做至少一项可用智能体',
    items: [
      { key: 'cap', title: '能力注册', icon: Wrench, required: false, deps: ['model'], page: '能力注册中心', tab: 'registry',
        desc: '按需——注册网管原子能力供超级智能体调用（不调工具可跳过）。' },
      { key: 'agent', title: '智能体接入并上架', icon: Bot, required: true, deps: ['model'], page: '智能体配置', tab: 'config',
        desc: '接入并上架至少一个智能体（API/MCP/Skill 或下游 Agent），平台即可对终端用户服务。' },
    ],
  },
];
const ALL = GROUPS.flatMap(g => g.items);
const TITLE = Object.fromEntries(ALL.map(i => [i.key, i.title]));
const REQUIRED = ALL.filter(i => i.required).map(i => i.key);

export default function InitWizard({ onNavigate }) {
  const [done, setDone] = useState({});
  const [toast, setToast] = useState('');
  const [retired, setRetired] = useState(false);   // 模拟"必做完成后清单从首页隐退"

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };
  const markDone = (key) => setDone(prev => ({ ...prev, [key]: true }));
  const goConfig = (item) => {
    if (item.tab && onNavigate) onNavigate(item.tab);            // demo 有该页 → 真跳转
    else showToast(`「${item.page}」为独立配置页，本 demo 暂未实现；实际部署中在该页完成配置，AI 引导式配置在该页内实时提供`);
  };
  const unmet = (item) => item.deps.filter(d => !done[d]);
  const statusOf = (item) => done[item.key] ? 'done' : (unmet(item).length ? 'locked' : 'todo');

  const reqDone = REQUIRED.filter(k => done[k]).length;
  const allReqDone = reqDone === REQUIRED.length;
  const totalDone = ALL.filter(i => done[i.key]).length;

  return (
    <div className="max-w-4xl mx-auto px-6 py-6">
      {toast && <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-xs px-4 py-2.5 rounded-lg shadow-lg max-w-sm">{toast}</div>}

      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Rocket className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">初始化配置清单</h2>
          <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">三期 P0</span>
        </div>
        <p className="text-sm text-gray-500">新部署实例的开箱配置清单：标注<strong>必做 / 按需</strong>，可按需挑着做；前置依赖未满足的项自动锁定并提示跳转。每项点「前往配置」进入对应页面完成，<strong>AI 引导式配置在各配置页面内实时提供</strong>（读字段元数据动态生成）。</p>
      </div>

      {/* 生命周期说明（业界主流：首页卡片 + 完成隐退，不占常驻菜单） */}
      <div className="mb-5 p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700 leading-relaxed">
        <strong>呈现位置（业界主流做法）：</strong>真实产品中本清单<strong>不固定占用常驻菜单</strong>，而是作为<strong>首页工作台顶部的 Setup 卡片</strong>（带进度），醒目但可收起；
        <strong>必做项全完成后自动隐退</strong>，首页恢复正常，仅在「设置」保留"重新查看部署清单"二级入口按需回访。下方进度条与"隐退"按钮即演示该生命周期。
      </div>

      {/* 完成后隐退态：模拟首页卡片消失、降级到设置入口 */}
      {retired && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-3">
          <CircleCheck className="w-6 h-6 text-green-500 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800">部署清单已完成，已从首页隐退</div>
            <div className="text-xs text-gray-400 mt-0.5">真实产品中此卡片不再出现在首页；如需回访，可在「设置 → 部署清单」重新打开。</div>
          </div>
          <button onClick={() => setRetired(false)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">从设置重新打开</button>
        </div>
      )}

      {!retired && <>


      {/* 总进度 */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{allReqDone ? '必做项已完成，平台可用 ✅' : `必做项 ${reqDone}/${REQUIRED.length} 完成`}</span>
          <span className="text-xs text-gray-400">共完成 {totalDone}/{ALL.length}</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${allReqDone ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${(reqDone / REQUIRED.length) * 100}%` }} />
        </div>
      </div>

      {allReqDone && (
        <div className="flex items-center gap-2 p-3 mb-5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CircleCheck className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">必做项已就绪，平台可正式对终端用户提供服务。按需项（知识库 / 能力注册）可随时补做。</span>
          <button onClick={() => setRetired(true)} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 whitespace-nowrap">完成，隐退清单</button>
        </div>
      )}

      {/* 分组清单 */}
      <div className="space-y-5">
        {GROUPS.map(g => (
          <div key={g.name}>
            <div className="flex items-baseline gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-700">{g.name}</h3>
              <span className="text-xs text-gray-400">{g.sub}</span>
            </div>
            <div className="space-y-2">
              {g.items.map(item => {
                const st = statusOf(item);
                const Icon = item.icon;
                const miss = unmet(item);
                return (
                  <div key={item.key} className={`bg-white border rounded-xl p-4 ${st === 'locked' ? 'border-gray-150 opacity-80' : 'border-gray-200'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${st === 'done' ? 'bg-green-100 text-green-600' : st === 'locked' ? 'bg-gray-100 text-gray-300' : 'bg-indigo-100 text-indigo-600'}`}>
                        {st === 'done' ? <Check className="w-5 h-5" /> : st === 'locked' ? <Lock className="w-4 h-4" /> : <Icon className="w-5 h-5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900">{item.title}</span>
                          {item.required
                            ? <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-500 rounded border border-red-100">必做</span>
                            : <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">按需</span>}
                          {st === 'done' && <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-600 rounded">已完成</span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                        {/* 对应配置页 + AI 引导归属说明 */}
                        <div className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> 对应页面：<span className="text-gray-500">{item.page}</span>
                          {item.tab ? <span className="text-indigo-400">（demo 可跳转）</span> : <span className="text-gray-300">（独立配置页，demo 暂无）</span>}
                          <span className="text-gray-300">· AI 引导在该页面内提供</span>
                        </div>

                        {/* 前置依赖未满足 → 确定性锁定 + 跳转提示 */}
                        {st === 'locked' && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            需先完成：{miss.map(d => `「${TITLE[d]}」`).join('、')}（平台后台已自动检测前置状态）
                          </div>
                        )}
                      </div>

                      {/* 右侧操作 */}
                      <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                        {st === 'locked'
                          ? <span className="text-xs text-gray-300 px-3 py-1.5">前置未满足</span>
                          : <button onClick={() => goConfig(item)} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 whitespace-nowrap">前往【{item.page.split(' ')[0]}】 <ArrowRight className="w-3 h-3" /></button>}
                        {st === 'done'
                          ? <span className="text-xs text-green-500">已完成</span>
                          : st !== 'locked' && <button onClick={() => markDone(item.key)} className="text-xs text-gray-400 hover:text-gray-600">标记完成（demo）</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-400 mt-5 flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" /> 前置依赖状态由平台后台自动检测（确定性规则），无需 SDK、无需项目组传数据——这是"配置引导"，与"上下文感知"（嵌宿主、传业务数据）是不同的能力。
      </p>
      </>}
    </div>
  );
}
